// Drawer admin para crear/editar una palabra de vocabulary.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import { listLevels, listLicenses, type CatalogLevel, type CatalogLicense } from '@/services/catalogs';
import {
  adminCreateVocabulary,
  adminUpdateVocabulary,
  type VocabularyItem,
} from '@/services/vocabulary';
import styles from './VocabularyEditorDrawer.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: VocabularyItem | null;
  onSaved: () => void;
}

export function VocabularyEditorDrawer({ open, onOpenChange, item, onSaved }: Props) {
  const isEdit = item !== null;

  const [levels, setLevels] = useState<CatalogLevel[]>([]);
  const [licenses, setLicenses] = useState<CatalogLicense[]>([]);

  const [word, setWord] = useState('');
  const [translation, setTranslation] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [levelId, setLevelId] = useState<number | null>(null);
  const [licenseSlug, setLicenseSlug] = useState<string>('');
  const [isActive, setIsActive] = useState(true);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    Promise.all([listLevels(), listLicenses()])
      .then(([lvls, lics]) => {
        setLevels(lvls);
        setLicenses(lics);
      })
      .catch(() => { /* ignore */ });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setWord(item.word);
      setTranslation(item.translation ?? '');
      setPronunciation(item.pronunciation ?? '');
      setImageUrl(item.image_url ?? '');
      setLevelId(item.level_id);
      setLicenseSlug(item.license_slug ?? '');
      setIsActive(item.is_active === 1);
    } else {
      setWord('');
      setTranslation('');
      setPronunciation('');
      setImageUrl('');
      setLevelId(null);
      setLicenseSlug('');
      setIsActive(true);
    }
  }, [open, item]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        word: word.trim(),
        translation: translation.trim() || null,
        pronunciation: pronunciation.trim() || null,
        image_url: imageUrl.trim() || null,
        level_id: levelId,
        license_slug: licenseSlug || null,
        is_active: isActive,
      };
      if (isEdit) {
        await adminUpdateVocabulary(item!.id, payload);
      } else {
        await adminCreateVocabulary(payload);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.titleHeader}>
              {isEdit ? 'Editar palabra' : 'Nueva palabra'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={16} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            <label className={styles.field}>
              <span>Palabra</span>
              <input value={word} onChange={(e) => setWord(e.target.value)} maxLength={200} placeholder="ej. roundabout" />
            </label>
            <label className={styles.field}>
              <span>Traducción</span>
              <input value={translation} onChange={(e) => setTranslation(e.target.value)} maxLength={255} placeholder="ej. rotonda" />
            </label>
            <label className={styles.field}>
              <span>Pronunciación</span>
              <input
                value={pronunciation}
                onChange={(e) => setPronunciation(e.target.value)}
                maxLength={255}
                placeholder="ej. /ˈraʊndəbaʊt/"
              />
            </label>
            <label className={styles.field}>
              <span>Imagen (URL)</span>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
              />
            </label>
            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>Nivel</span>
                <select value={levelId ?? ''} onChange={(e) => setLevelId(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">—</option>
                  {levels.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Licencia (visibilidad)</span>
                <select value={licenseSlug} onChange={(e) => setLicenseSlug(e.target.value)}>
                  <option value="">Todas</option>
                  {licenses.map((l) => (
                    <option key={l.slug} value={l.slug}>{l.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span>Activa (entra en el sorteo de palabra del día)</span>
            </label>
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <footer className={styles.footer}>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={save} disabled={busy || !word.trim()}>
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
